import {
  CHARGE_KINDS,
  CHARGE_STATUSES,
  ChargeKind,
  ChargeStatus,
  Currency,
  PaymentChargeListResponse,
  PaymentChargeListRow,
  PaymentChargeListRowDto,
} from '@app/shared/models/polipay-payment.model';

/** Convierte un campo numérico que puede llegar como string o número. Ausente → 0. */
function toNum(raw: number | string | null | undefined): number {
  if (raw === null || raw === undefined) return 0;
  const n = typeof raw === 'string' ? Number(raw) : raw;
  return Number.isFinite(n) ? n : 0;
}

/**
 * Igual que `toNum` pero preserva `null` explícito.
 * Se usa para `fee`/`tax`/`net_amount`: el contrato dice que `null` significa
 * "el gateway aún no desglosó este cargo" y NO debe pintarse como `$0.00`.
 */
function toNumOrNull(raw: number | string | null | undefined): number | null {
  if (raw === null) return null;
  if (raw === undefined) return null;
  const n = typeof raw === 'string' ? Number(raw) : raw;
  return Number.isFinite(n) ? n : null;
}

// Igualdad EXACTA: el contrato da los valores con su capitalización y el backend no
// los cambia. Lo que no esté en el catálogo NO se propaga: la pantalla lo verá como
// desconocido y pintará la etiqueta traducida de desconocido, nunca el string crudo.
const STATUS_SET: ReadonlySet<string> = new Set(CHARGE_STATUSES);
const KIND_SET: ReadonlySet<string> = new Set(CHARGE_KINDS);

function toStatus(raw: unknown): ChargeStatus | '' {
  return typeof raw === 'string' && STATUS_SET.has(raw) ? (raw as ChargeStatus) : '';
}

function toKind(raw: unknown): ChargeKind | '' {
  return typeof raw === 'string' && KIND_SET.has(raw) ? (raw as ChargeKind) : '';
}

/**
 * Normaliza un renglón de `GET /api/v1/payments/charges/` (§2 2026-09-22).
 *
 * Acepta snake_case y camelCase: el contrato confirmó snake_case en §2 pero
 * `POST /charges/` sigue respondiendo camelCase, así que se leen ambas formas.
 *
 * ⚠️ `fee`/`tax`/`net_amount` traen hasta 6 decimales — no truncar al almacenar.
 * ⚠️ Los cargos declinados (`status: 'Failed'`) devuelven `fee`/`tax`/`netAmount`
 * positivos (bug conocido del gateway, §Huecos 5). Filtrar por `Succeeded` antes
 * de sumar.
 */
export function mapChargeListRow(dto: PaymentChargeListRowDto): PaymentChargeListRow {
  return {
    id: dto.id ?? '',
    idTransaction: dto.id_transaction ?? dto.idTransaction ?? '',
    kind: toKind(dto.kind),
    amount: toNum(dto.amount),
    fee: toNumOrNull(dto.fee),
    tax: toNumOrNull(dto.tax),
    netAmount: toNumOrNull(dto.net_amount ?? dto.netAmount),
    currency: (dto.currency ?? 'MXN') as Currency,
    status: toStatus(dto.status),
    customerEmail: (dto.customer_email ?? dto.customerEmail ?? '').trim(),
    description: (dto.description ?? '').trim(),
    failureReason: (dto.failure_reason ?? dto.failureReason ?? '').trim(),
    idCompany: dto.id_company ?? dto.idCompany ?? null,
    companyName: (dto.company_name ?? dto.companyName ?? '').trim(),
    createdAt: dto.created_at ?? dto.createdAt ?? '',
    // Solo las filas QR los traen; en las demás llegan `null` (contrato cobro QR).
    payerDisplayName: (dto.payer_display_name ?? '').trim(),
    wallet: (dto.wallet ?? '').trim(),
  };
}

/**
 * Desenvuelve la respuesta del listado. `/links/` devuelve `{count, results}` y es
 * lo que asumimos acá, pero el contrato de `/charges/` no lo dijo, así que también
 * se tolera un arreglo pelado (y en ese caso el total es el largo del arreglo).
 */
export function toChargeListResponse(raw: unknown): PaymentChargeListResponse {
  if (Array.isArray(raw)) {
    const results = raw.map((row) => mapChargeListRow(row as PaymentChargeListRowDto));
    return { count: results.length, results };
  }

  const envelope = (raw ?? {}) as { count?: number; results?: PaymentChargeListRowDto[] };
  const results = (envelope.results ?? []).map(mapChargeListRow);
  return { count: envelope.count ?? results.length, results };
}
